import {
	importAsset,
	getImportReferenceData,
	type AssetImportRow,
} from "@/services/asset-service";
import {
	InboxOutlined,
	CheckCircleOutlined,
	CloseCircleOutlined,
	InfoCircleOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import {
	Modal,
	Upload,
	Table,
	Select,
	Button,
	Steps,
	message,
	Alert,
	Progress,
	Space,
	Typography,
	Tag,
	Tooltip,
} from "antd";
import { useState, useMemo, useEffect } from "react";
import * as XLSX from "xlsx";

const { Dragger } = Upload;
const { Text } = Typography;

// Fields available for mapping - assets have more fields than locations
const ASSET_FIELDS = [
	{ value: "", label: "-- Skip --" },
	// Location resolution (at least one required)
	{ value: "location_name", label: "Location Name", group: "location", required: true },
	{ value: "location_address", label: "Location Address", group: "location" },
	{ value: "location_store_number", label: "Store Number", group: "location" },
	// Asset type (required)
	{ value: "asset_type", label: "Asset Type", required: true },
	// Asset details
	{ value: "asset_tag", label: "Asset Tag" },
	{ value: "manufacturer", label: "Manufacturer" },
	{ value: "model_number", label: "Model Number" },
	{ value: "serial_number", label: "Serial Number" },
	{ value: "condition", label: "Condition" },
	{ value: "date_of_birth", label: "Date of Birth / Install Date" },
	{ value: "manufacturer_date", label: "Manufacturer Date" },
	{ value: "general_notes", label: "General Notes" },
	{ value: "power_source", label: "Power Source" },
	{ value: "equipment_area", label: "Equipment Area" },
];

// At least one location field + asset_type are required
const REQUIRED_FIELDS = ["asset_type"];
// At least one of these must be mapped
const LOCATION_FIELDS_GROUP = ["location_name", "location_address", "location_store_number"];

interface ImportAssetsModalProps {
	open: boolean;
	onClose: () => void;
	onImportComplete: () => void;
}

/**
 * Auto-map spreadsheet headers to asset fields using fuzzy matching
 */
const autoMapColumns = (headers: string[]): Record<number, string> => {
	const mapping: Record<number, string> = {};
	const aliases: Record<string, string[]> = {
		location_name: [
			"location name", "location", "site name", "site",
			"facility", "facility name", "building", "building name",
		],
		location_address: [
			"location address", "address", "street", "street address",
			"address1", "address 1",
		],
		location_store_number: [
			"store number", "store_number", "store #", "store no",
			"site number", "site #", "site id", "location id", "store",
		],
		asset_type: [
			"asset type", "asset_type", "type", "equipment type",
			"equipment", "category",
		],
		asset_tag: [
			"asset tag", "asset_tag", "tag", "asset id", "tag number",
			"tag #", "asset #", "asset number", "barcode",
		],
		manufacturer: [
			"manufacturer", "mfg", "make", "brand", "oem",
			"manufacturer name",
		],
		model_number: [
			"model number", "model_number", "model", "model #",
			"model no",
		],
		serial_number: [
			"serial number", "serial_number", "serial", "serial #",
			"serial no", "s/n",
		],
		condition: [
			"condition", "status", "asset condition",
			"equipment condition",
		],
		date_of_birth: [
			"date of birth", "dob", "install date", "installation date",
			"installed", "install", "date installed", "in service date",
			"in-service date",
		],
		manufacturer_date: [
			"manufacturer date", "mfg date", "manufacture date",
			"manufactured", "build date", "year built",
		],
		general_notes: [
			"general notes", "notes", "comments", "description",
			"remarks",
		],
		power_source: [
			"power source", "power", "energy source", "fuel",
			"fuel type", "power type",
		],
		equipment_area: [
			"equipment area", "area", "zone", "section",
			"department",
		],
	};

	const used = new Set<string>();

	headers.forEach((header, idx) => {
		const normalized = header.toLowerCase().trim();
		for (const [field, aliasList] of Object.entries(aliases)) {
			if (!used.has(field) && aliasList.includes(normalized)) {
				mapping[idx] = field;
				used.add(field);
				break;
			}
		}
	});

	return mapping;
};

const ImportAssetsModal = ({ open, onClose, onImportComplete }: ImportAssetsModalProps) => {
	const [step, setStep] = useState(0);
	const [headers, setHeaders] = useState<string[]>([]);
	const [rows, setRows] = useState<string[][]>([]);
	const [columnMapping, setColumnMapping] = useState<Record<number, string>>({});
	const [importing, setImporting] = useState(false);
	const [importProgress, setImportProgress] = useState(0);
	const [importResults, setImportResults] = useState<{
		success: number;
		failed: number;
		errors: string[];
	}>({ success: 0, failed: 0, errors: [] });
	const [fileName, setFileName] = useState("");
	const [referenceData, setReferenceData] = useState<{
		assetTypes: string[];
		locations: string[];
	} | null>(null);

	// Load reference data when modal opens
	useEffect(() => {
		if (open && !referenceData) {
			getImportReferenceData()
				.then(setReferenceData)
				.catch(() => {
					console.error("Failed to load reference data");
				});
		}
	}, [open, referenceData]);

	const reset = () => {
		setStep(0);
		setHeaders([]);
		setRows([]);
		setColumnMapping({});
		setImporting(false);
		setImportProgress(0);
		setImportResults({ success: 0, failed: 0, errors: [] });
		setFileName("");
	};

	const handleClose = () => {
		reset();
		onClose();
	};

	// Parse uploaded file
	const handleFile = (file: File) => {
		setFileName(file.name);
		const reader = new FileReader();
		reader.onload = (e) => {
			try {
				const data = new Uint8Array(e.target?.result as ArrayBuffer);
				const workbook = XLSX.read(data, { type: "array" });
				const sheet = workbook.Sheets[workbook.SheetNames[0]];
				const json: string[][] = XLSX.utils.sheet_to_json(sheet, {
					header: 1,
					defval: "",
				});

				if (json.length < 2) {
					message.error("File must have a header row and at least one data row.");
					return;
				}

				const fileHeaders = json[0].map(String);
				const fileRows = json
					.slice(1)
					.filter((row) => row.some((cell) => String(cell).trim() !== ""));

				if (fileRows.length === 0) {
					message.error("No data rows found in the file.");
					return;
				}

				setHeaders(fileHeaders);
				setRows(fileRows);
				setColumnMapping(autoMapColumns(fileHeaders));
				setStep(1);
			} catch {
				message.error("Failed to parse file. Please ensure it's a valid Excel or CSV file.");
			}
		};
		reader.readAsArrayBuffer(file);
		return false; // Prevent default upload
	};

	// Check required field mapping
	const missingRequired = useMemo(() => {
		const mapped = new Set(Object.values(columnMapping));
		return REQUIRED_FIELDS.filter((f) => !mapped.has(f));
	}, [columnMapping]);

	// Check if at least one location field is mapped
	const hasLocationField = useMemo(() => {
		const mapped = new Set(Object.values(columnMapping));
		return LOCATION_FIELDS_GROUP.some((f) => mapped.has(f));
	}, [columnMapping]);

	// Validation warnings for mapped data
	const validationWarnings = useMemo(() => {
		if (!referenceData || rows.length === 0) return [];
		const warnings: string[] = [];
		const mapped = Object.entries(columnMapping);

		// Check asset types against reference data
		const assetTypeCol = mapped.find(([, field]) => field === "asset_type");
		if (assetTypeCol) {
			const colIdx = Number(assetTypeCol[0]);
			const uniqueTypes = new Set(
				rows.map((row) => String(row[colIdx] ?? "").trim().toLowerCase()).filter(Boolean)
			);
			const knownTypes = new Set(referenceData.assetTypes.map((t) => t.toLowerCase()));
			const unknownTypes = [...uniqueTypes].filter((t) => !knownTypes.has(t));
			if (unknownTypes.length > 0) {
				warnings.push(
					`${unknownTypes.length} asset type(s) not found in your organization: ${unknownTypes.slice(0, 5).join(", ")}${unknownTypes.length > 5 ? "..." : ""}. These rows will fail to import.`
				);
			}
		}

		// Check locations against reference data
		const locationNameCol = mapped.find(([, field]) => field === "location_name");
		if (locationNameCol) {
			const colIdx = Number(locationNameCol[0]);
			const uniqueLocations = new Set(
				rows.map((row) => String(row[colIdx] ?? "").trim().toLowerCase()).filter(Boolean)
			);
			const knownLocations = new Set(referenceData.locations.map((l) => l.toLowerCase()));
			const unknownLocations = [...uniqueLocations].filter((l) => !knownLocations.has(l));
			if (unknownLocations.length > 0) {
				warnings.push(
					`${unknownLocations.length} location(s) not found: ${unknownLocations.slice(0, 5).join(", ")}${unknownLocations.length > 5 ? "..." : ""}. These rows will fail unless matched by address or store number.`
				);
			}
		}

		return warnings;
	}, [referenceData, rows, columnMapping]);

	// Preview data using current mapping
	const previewData = useMemo(() => {
		return rows.slice(0, 5).map((row, rowIdx) => {
			const mapped: Record<string, string> = { key: String(rowIdx) };
			Object.entries(columnMapping).forEach(([colIdx, field]) => {
				if (field) {
					mapped[field] = String(row[Number(colIdx)] ?? "");
				}
			});
			return mapped;
		});
	}, [rows, columnMapping]);

	const previewColumns = useMemo(() => {
		return Object.values(columnMapping)
			.filter(Boolean)
			.map((field) => ({
				title: ASSET_FIELDS.find((f) => f.value === field)?.label || field,
				dataIndex: field,
				key: field,
				ellipsis: true,
				width: 140,
			}));
	}, [columnMapping]);

	// Handle import
	const handleImport = async () => {
		if (missingRequired.length > 0 || !hasLocationField) {
			message.error("Please map all required fields before importing.");
			return;
		}

		setImporting(true);
		setStep(2);
		setImportProgress(0);

		let success = 0;
		let failed = 0;
		const errors: string[] = [];

		for (let i = 0; i < rows.length; i++) {
			const row = rows[i];
			const assetRow: AssetImportRow = {};

			Object.entries(columnMapping).forEach(([colIdx, field]) => {
				if (field) {
					const val = String(row[Number(colIdx)] ?? "").trim();
					if (val) {
						(assetRow as any)[field] = val;
					}
				}
			});

			try {
				await importAsset(assetRow);
				success++;
			} catch (err: any) {
				failed++;
				const identifier =
					assetRow.asset_tag ||
					assetRow.serial_number ||
					assetRow.asset_type ||
					`Row ${i + 2}`;
				errors.push(`${identifier}: ${err.message || "Unknown error"}`);
			}

			setImportProgress(Math.round(((i + 1) / rows.length) * 100));
		}

		setImportResults({ success, failed, errors });
		setImporting(false);

		if (success > 0) {
			onImportComplete();
		}
	};

	// Update a single column mapping
	const updateMapping = (colIdx: number, field: string) => {
		setColumnMapping((prev) => {
			const next = { ...prev };
			if (field === "") {
				delete next[colIdx];
			} else {
				// Remove any previous use of this field
				for (const key of Object.keys(next)) {
					if (next[Number(key)] === field) {
						delete next[Number(key)];
					}
				}
				next[colIdx] = field;
			}
			return next;
		});
	};

	// Mapping columns table
	const mappingColumns = [
		{
			title: "Spreadsheet Column",
			dataIndex: "header",
			key: "header",
			width: 200,
			render: (text: string) => <Text strong>{text}</Text>,
		},
		{
			title: "Sample Data",
			dataIndex: "sample",
			key: "sample",
			width: 200,
			render: (text: string) => (
				<Text type="secondary">{text || <i>empty</i>}</Text>
			),
		},
		{
			title: "Maps To",
			dataIndex: "mapping",
			key: "mapping",
			width: 220,
			render: (_: any, record: any) => {
				const currentField = columnMapping[record.colIdx];
				const fieldDef = ASSET_FIELDS.find((f) => f.value === currentField);
				return (
					<Space direction="vertical" size={0} style={{ width: "100%" }}>
						<Select
							value={currentField || ""}
							onChange={(val) => updateMapping(record.colIdx, val)}
							options={ASSET_FIELDS.map((f) => ({
								...f,
								label: (
									<span>
										{f.label}
										{f.required && (
											<span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>
										)}
									</span>
								),
								disabled:
									f.value !== "" &&
									f.value !== currentField &&
									Object.values(columnMapping).includes(f.value),
							}))}
							style={{ width: "100%" }}
							size="small"
						/>
						{fieldDef?.group === "location" && (
							<Text type="secondary" style={{ fontSize: 11 }}>
								Used to find existing location
							</Text>
						)}
					</Space>
				);
			},
		},
	];

	const mappingData = headers.map((header, idx) => ({
		key: String(idx),
		colIdx: idx,
		header,
		sample: rows.length > 0 ? String(rows[0][idx] ?? "") : "",
	}));

	const allValid = missingRequired.length === 0 && hasLocationField;

	return (
		<Modal
			open={open}
			onCancel={handleClose}
			title="Import Assets from Spreadsheet"
			width={800}
			footer={
				step === 0 ? null : step === 1 ? (
					<Space>
						<Button onClick={() => setStep(0)}>Back</Button>
						<Button
							type="primary"
							onClick={handleImport}
							disabled={!allValid}
							style={{ backgroundColor: "#92278F", borderColor: "#92278F" }}
						>
							Import {rows.length} Asset{rows.length !== 1 ? "s" : ""}
						</Button>
					</Space>
				) : (
					<Button
						onClick={handleClose}
						type="primary"
						style={{ backgroundColor: "#92278F", borderColor: "#92278F" }}
					>
						Done
					</Button>
				)
			}
			destroyOnClose
		>
			<Steps
				current={step}
				size="small"
				style={{ marginBottom: 24 }}
				items={[
					{ title: "Upload" },
					{ title: "Map Columns" },
					{ title: "Import" },
				]}
			/>

			{/* Step 1: Upload */}
			{step === 0 && (
				<div>
					<Dragger
						accept=".xlsx,.xls,.csv"
						beforeUpload={handleFile}
						showUploadList={false}
						style={{ padding: "20px 0" }}
					>
						<p className="ant-upload-drag-icon">
							<InboxOutlined style={{ color: "#92278F", fontSize: 48 }} />
						</p>
						<p className="ant-upload-text">Click or drag a file to upload</p>
						<p className="ant-upload-hint">
							Supports .xlsx, .xls, and .csv files. First row must be column headers.
						</p>
					</Dragger>

					<Alert
						type="info"
						showIcon
						icon={<InfoCircleOutlined />}
						style={{ marginTop: 16 }}
						message="Asset Import Requirements"
						description={
							<ul style={{ margin: "4px 0", paddingLeft: 20 }}>
								<li>
									<strong>Location</strong> — At least one column must match an existing location (by name, address, or store number)
								</li>
								<li>
									<strong>Asset Type</strong> — Must match an existing asset type in your organization
								</li>
								<li>
									Manufacturers will be auto-created if they don't exist yet
								</li>
							</ul>
						}
					/>
				</div>
			)}

			{/* Step 2: Map Columns */}
			{step === 1 && (
				<div>
					<div style={{ marginBottom: 12 }}>
						<Text type="secondary">
							File: <Text strong>{fileName}</Text> — {rows.length} row
							{rows.length !== 1 ? "s" : ""} found
						</Text>
					</div>

					{/* Required fields warning */}
					{missingRequired.length > 0 && (
						<Alert
							type="warning"
							showIcon
							style={{ marginBottom: 12 }}
							message={`Required fields not mapped: ${missingRequired
								.map((f) => ASSET_FIELDS.find((af) => af.value === f)?.label)
								.join(", ")}`}
						/>
					)}

					{!hasLocationField && (
						<Alert
							type="warning"
							showIcon
							style={{ marginBottom: 12 }}
							message="At least one location field must be mapped (Location Name, Location Address, or Store Number)"
						/>
					)}

					{/* Validation warnings */}
					{validationWarnings.map((warning, idx) => (
						<Alert
							key={idx}
							type="warning"
							showIcon
							icon={<WarningOutlined />}
							style={{ marginBottom: 8 }}
							message={warning}
						/>
					))}

					{/* Reference data hints */}
					{referenceData && (
						<div style={{ marginBottom: 12, display: "flex", gap: 16 }}>
							<Tooltip
								title={
									referenceData.assetTypes.length > 0
										? referenceData.assetTypes.slice(0, 20).join(", ") +
											(referenceData.assetTypes.length > 20 ? "..." : "")
										: "No asset types found"
								}
							>
								<Tag color="blue">
									{referenceData.assetTypes.length} asset type{referenceData.assetTypes.length !== 1 ? "s" : ""} available
								</Tag>
							</Tooltip>
							<Tooltip
								title={
									referenceData.locations.length > 0
										? referenceData.locations.slice(0, 20).join(", ") +
											(referenceData.locations.length > 20 ? "..." : "")
										: "No locations found"
								}
							>
								<Tag color="green">
									{referenceData.locations.length} location{referenceData.locations.length !== 1 ? "s" : ""} available
								</Tag>
							</Tooltip>
						</div>
					)}

					<Table
						dataSource={mappingData}
						columns={mappingColumns}
						pagination={false}
						size="small"
						style={{ marginBottom: 16 }}
						scroll={{ y: 280 }}
					/>

					{previewColumns.length > 0 && (
						<>
							<Text strong style={{ display: "block", marginBottom: 8 }}>
								Preview (first 5 rows):
							</Text>
							<Table
								dataSource={previewData}
								columns={previewColumns}
								pagination={false}
								size="small"
								scroll={{ x: "max-content" }}
							/>
						</>
					)}
				</div>
			)}

			{/* Step 3: Import Progress / Results */}
			{step === 2 && (
				<div style={{ textAlign: "center", padding: "20px 0" }}>
					{importing ? (
						<>
							<Progress
								type="circle"
								percent={importProgress}
								strokeColor="#92278F"
							/>
							<p style={{ marginTop: 16, color: "#888" }}>
								Importing assets...{" "}
								{Math.round((importProgress / 100) * rows.length)} of{" "}
								{rows.length}
							</p>
						</>
					) : (
						<>
							<div style={{ marginBottom: 16 }}>
								{importResults.success > 0 && (
									<div style={{ marginBottom: 8 }}>
										<CheckCircleOutlined
											style={{ color: "#52c41a", fontSize: 18, marginRight: 8 }}
										/>
										<Text strong>{importResults.success}</Text> asset
										{importResults.success !== 1 ? "s" : ""} imported
										successfully
									</div>
								)}
								{importResults.failed > 0 && (
									<div>
										<CloseCircleOutlined
											style={{ color: "#ff4d4f", fontSize: 18, marginRight: 8 }}
										/>
										<Text strong>{importResults.failed}</Text> asset
										{importResults.failed !== 1 ? "s" : ""} failed
									</div>
								)}
							</div>
							{importResults.errors.length > 0 && (
								<Alert
									type="error"
									showIcon
									message="Import Errors"
									description={
										<div
											style={{
												maxHeight: 200,
												overflow: "auto",
												textAlign: "left",
											}}
										>
											{importResults.errors.map((err, i) => (
												<div
													key={i}
													style={{ fontSize: 12, marginBottom: 4 }}
												>
													{err}
												</div>
											))}
										</div>
									}
								/>
							)}
						</>
					)}
				</div>
			)}
		</Modal>
	);
};

export { ImportAssetsModal };
